/**
 * The panel the table's controls open: columns, sort, filters, the date range.
 *
 * ONE PANEL, FOUR CALLERS. Each of those used to be the place a hand-rolled
 * popover would grow, with its own outside click, its own Escape and its own
 * idea of where to sit, and the columns picker already carried one. They differ
 * in what is inside and in how wide they are, and in nothing else, so the
 * behaviour lives here once.
 *
 * It borrows `placeMenu` and the portal from `Menu`, for the reason `Menu`
 * gives: a panel positioned inside the card composites in the card's stacking
 * context, and a sticky table header further down paints straight through it.
 *
 * WHY IT IS A NON-MODAL DIALOG. These panels hold checkboxes, selects and
 * search fields, so a `menu` role would promise arrow keys they do not answer
 * to. Nothing is trapped: Tab walks the controls in order, and Tab past either
 * end closes the panel and puts focus back on the trigger, which is where the
 * browser's own Tab order carries on from.
 *
 * THERE IS NO SCRIM, which is what keeps it clear of the defect the reference
 * console ships, where the click after Escape lands on an overlay that is still
 * fading out. An outside press closes the panel on `mousedown` and the press
 * carries on to whatever was under it, so the click is never swallowed.
 */
import type { CSSProperties, KeyboardEvent, ReactNode, RefObject } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'
import { useOnEscape } from '../lib/hooks'
import { placeMenu } from './Menu'
import type { MenuAlign, MenuSpot } from './Menu'

// A layout effect is what keeps the panel from painting once at stale
// coordinates. On a server render there is no layout and React warns, so the
// plain effect stands in there, where the panel is closed anyway.
const useBeforePaint = typeof window === 'undefined' ? useEffect : useLayoutEffect

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'

export interface TablePopoverProps {
  open: boolean
  /** `returnFocus` is true for Escape and Tab, false for an outside press, a scroll or a resize. */
  onClose: (returnFocus: boolean) => void
  triggerRef: RefObject<HTMLElement>
  /** In pixels. The panel is placed by arithmetic, so the width is a number and not a class. */
  width: number
  /** Lets the panel grow past `width` to fit its content. The filter panel does. */
  grow?: boolean
  /**
   * Lay the children side by side. A prop and not a class, because `cn` joins
   * without merging and Tailwind emits `flex-col` after `flex-row`, so a
   * `flex-row` passed in className lost and the filter panel's two panes
   * stacked.
   */
  row?: boolean
  align?: MenuAlign
  /** The accessible name, since the trigger is not inside the panel. */
  label: string
  className?: string
  children: ReactNode
}

export function TablePopover({
  open,
  onClose,
  triggerRef,
  width,
  grow = false,
  row = false,
  align = 'start',
  label,
  className,
  children,
}: TablePopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [spot, setSpot] = useState<MenuSpot | null>(null)

  // Callers write `onClose={() => ...}` inline. Read it through a ref so the
  // listeners below do not re-subscribe on every render.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const close = useCallback(
    (returnFocus: boolean) => {
      onCloseRef.current(returnFocus)
      if (returnFocus) triggerRef.current?.focus({ preventScroll: true })
    },
    [triggerRef],
  )

  // Before paint, so the panel is never shown once at last time's coordinates.
  useBeforePaint(() => {
    if (!open) {
      setSpot(null)
      return
    }
    const anchor = triggerRef.current?.getBoundingClientRect()
    if (anchor === undefined) return
    setSpot(
      placeMenu(anchor, {
        placement: 'bottom',
        align,
        width,
        viewport: { width: window.innerWidth, height: window.innerHeight },
      }),
    )
  }, [open, align, width, triggerRef])

  useOnEscape(open, () => close(true))

  useEffect(() => {
    if (!open) return undefined

    const onPointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (panelRef.current?.contains(target) === true) return
      // The trigger toggles. Counting it as outside would close here and
      // reopen in the trigger's own handler.
      if (triggerRef.current?.contains(target) === true) return
      // A select inside the panel draws its list into the body. A press on
      // that list is a press inside the panel as far as anybody is concerned.
      if (target instanceof Element && target.closest('[role="listbox"]') !== null) return
      close(false)
    }

    // A rect taken before a scroll points at nothing afterwards, so the panel
    // closes, except for a scroll of a list inside the panel itself.
    const onScroll = (event: Event): void => {
      const target = event.target
      if (target instanceof Node && panelRef.current?.contains(target) === true) return
      if (target instanceof Element && target.closest('[role="listbox"]') !== null) return
      close(false)
    }
    const onResize = (): void => close(false)

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open, close, triggerRef])

  // Focus lands in the panel, or the keyboard is left behind the button it
  // just pressed with no way into what opened.
  useEffect(() => {
    if (spot === null) return
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true })
  }, [spot])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Tab') return
    const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
    const first = items[0]
    const last = items[items.length - 1]
    const leavingBack = event.shiftKey && document.activeElement === first
    const leavingForward = !event.shiftKey && document.activeElement === last
    if (items.length === 0 || leavingBack || leavingForward) {
      event.preventDefault()
      close(true)
    }
  }

  if (!open || spot === null) return null

  const style: CSSProperties = grow
    ? { left: spot.left, top: spot.top, bottom: spot.bottom, minWidth: spot.width, maxHeight: spot.maxHeight }
    : { left: spot.left, top: spot.top, bottom: spot.bottom, width: spot.width, maxHeight: spot.maxHeight }

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={label}
      style={style}
      onKeyDown={onKeyDown}
      className={cn(
        'fixed z-popover flex animate-pop-in overflow-auto border border-line bg-surface',
        row ? 'flex-row' : 'flex-col',
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  )
}

/**
 * Open state for a panel hung off a button: the ref for the button and the
 * three things the button and the panel need between them.
 */
export function usePopover(): {
  open: boolean
  triggerRef: RefObject<HTMLButtonElement>
  toggle: () => void
  close: () => void
} {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const toggle = useCallback(() => setOpen((was) => !was), [])
  const close = useCallback(() => setOpen(false), [])
  return { open, triggerRef, toggle, close }
}
